import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Uses the shared /api/profile endpoints. Single sign-on token like the rest of the app.
const api = axios.create({ baseURL: '/api/profile', withCredentials: true });
api.interceptors.request.use(cfg => { const t = localStorage.getItem('flowup_token'); if (t) cfg.headers.Authorization = `Bearer ${t}`; return cfg; });
const get = (u) => api({ method: 'get', url: u }).then(r => r.data);
const put = (u, d) => api({ method: 'put', url: u, data: d }).then(r => r.data);
const del = (u) => api({ method: 'delete', url: u }).then(r => r.data);

const ACCENT = '#5B5BD6';
const inp = { width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box' };
const lbl = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--muted)', marginBottom: 6, display: 'block' };
const btn = (bg = ACCENT, c = '#fff') => ({ padding: '10px 20px', borderRadius: 9, border: 'none', background: bg, color: c, fontSize: 14, fontWeight: 700, cursor: 'pointer' });

export default function UserInformationPage() {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [f, setF] = useState({ job_title: '', department: '', phone: '', staff_no: '', position_title: '', function_name: '', salary_grade: '', line_manager_name: '', join_date: '', date_to_position: '', date_to_sct: '', date_to_salary_group: '' });
  const [managers, setManagers] = useState([]);
  const [hasSig, setHasSig] = useState(false);
  const [sigBust, setSigBust] = useState(Date.now());
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const show = (m) => { setToast(m); setTimeout(() => setToast(''), 3000); };
  const token = localStorage.getItem('flowup_token');

  useEffect(() => {
    get('/').then(d => {
      setP(d);
      setF({
        job_title: d.job_title || '', department: d.department || '', phone: d.phone || '',
        staff_no: d.staff_no || '', position_title: d.position_title || '', function_name: d.function_name || '',
        salary_grade: d.salary_grade || '', line_manager_name: d.line_manager_name || '',
        join_date: d.join_date || '', date_to_position: d.date_to_position || '', date_to_sct: d.date_to_sct || '', date_to_salary_group: d.date_to_salary_group || '',
      });
      setManagers(d.managers || []);
      setHasSig(!!d.has_signature);
    }).catch(() => setP(null));
  }, []);

  if (p && p.is_super) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 420 }}>
          <h2 style={{ color: 'var(--text)' }}>User Information</h2>
          <p style={{ color: 'var(--muted2)' }}>Super admins configure the system and don't maintain a personal profile here.</p>
          <button onClick={() => navigate('/hub')} style={btn()}>← Back to Hub</button>
        </div>
      </div>
    );
  }

  const save = async () => { setSaving(true); try { const d = await put('/', f); setP(d); show('Information saved.'); } catch (e) { show(e?.response?.data?.error || 'Failed'); } setSaving(false); };
  const uploadSig = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!file.name.toLowerCase().endsWith('.png')) { show('Signature must be a PNG image.'); return; }
    const fd = new FormData(); fd.append('signature', file);
    try { await api({ method: 'post', url: '/signature', data: fd, headers: { 'Content-Type': 'multipart/form-data' } }); setHasSig(true); setSigBust(Date.now()); show('Signature uploaded.'); }
    catch (err) { show('Upload failed'); }
  };
  const removeSig = async () => { try { await del('/signature'); setHasSig(false); } catch (e) {} };

  const initial = (p?.full_name || '?').charAt(0).toUpperCase();
  const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 26, boxShadow: '0 1px 3px rgba(16,24,40,0.04)' };
  const sectionTitle = (icon, title, sub) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
      <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(91,91,214,0.10)', border: '1px solid rgba(91,91,214,0.20)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>{title}</div>
        {sub && <div style={{ fontSize: 12, color: 'var(--muted2)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );

  return (
    <div style={{ height: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Fixed header */}
      <div style={{ flexShrink: 0, background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '14px 28px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <button onClick={() => navigate('/hub')} style={{ ...btn('var(--surface2)', 'var(--text)'), padding: '7px 14px', fontSize: 13 }}>← Hub</button>
        <div>
          <div style={{ fontSize: 18, fontWeight: 850, color: 'var(--text)' }}>My Information</div>
          <div style={{ fontSize: 12, color: 'var(--muted2)' }}>Saved once and used across all applications</div>
        </div>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <div style={{ maxWidth: 'min(94vw, 1100px)', margin: '0 auto', padding: '28px clamp(20px, 2.5vw, 36px) 80px' }}>
          {/* Identity banner */}
          <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 18, marginBottom: 22, background: 'linear-gradient(120deg, var(--surface), var(--surface2))' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#5B5BD6,#7C3AED)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 800, flexShrink: 0 }}>{initial}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 19, fontWeight: 850, color: 'var(--text)' }}>{p?.full_name || '—'}</div>
              <div style={{ fontSize: 13, color: 'var(--muted2)' }}>{[f.job_title, f.department].filter(Boolean).join(' · ') || 'Add your job title and department below'}</div>
              <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>{p?.email}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 92, height: 50, border: '1px dashed var(--border)', borderRadius: 8, background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {hasSig ? <img src={`/api/profile/signature?token=${token}&b=${sigBust}`} alt="signature" style={{ maxWidth: '100%', maxHeight: 42 }} /> : <span style={{ fontSize: 10, color: 'var(--muted)' }}>No signature</span>}
              </div>
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Signature</div>
            </div>
          </div>

          {/* General */}
          <div style={{ ...card, marginBottom: 22 }}>
            {sectionTitle('👤', 'General', 'Name and email come from your account and cannot be changed here.')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div><label style={lbl}>Full Name</label><input style={{ ...inp, background: 'var(--surface2)', color: 'var(--muted2)' }} value={p?.full_name || ''} disabled /></div>
              <div><label style={lbl}>Email</label><input style={{ ...inp, background: 'var(--surface2)', color: 'var(--muted2)' }} value={p?.email || ''} disabled /></div>
              <div><label style={lbl}>Job Title</label><input style={inp} value={f.job_title} onChange={e => setF({ ...f, job_title: e.target.value })} /></div>
              <div><label style={lbl}>Department</label><input style={inp} value={f.department} onChange={e => setF({ ...f, department: e.target.value })} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Phone <span style={{ textTransform: 'none', fontWeight: 400 }}>(optional)</span></label><input style={inp} value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} /></div>
            </div>
          </div>

          {/* Performance Plan Details */}
          <div style={{ ...card, marginBottom: 22 }}>
            {sectionTitle('📋', 'Performance Plan Details', "These fill the header of your Individual Performance Plan — general and apply to every year.")}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div><label style={lbl}>Staff No</label><input style={inp} value={f.staff_no} onChange={e => setF({ ...f, staff_no: e.target.value })} /></div>
              <div><label style={lbl}>Position Title</label><input style={inp} value={f.position_title} onChange={e => setF({ ...f, position_title: e.target.value })} /></div>
              <div><label style={lbl}>Function</label><input style={inp} value={f.function_name} onChange={e => setF({ ...f, function_name: e.target.value })} /></div>
              <div><label style={lbl}>Salary Grade</label><input style={inp} value={f.salary_grade} onChange={e => setF({ ...f, salary_grade: e.target.value })} /></div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>Line Manager</label>
                <select style={inp} value={f.line_manager_name} onChange={e => setF({ ...f, line_manager_name: e.target.value })}>
                  <option value="">— Select line manager —</option>
                  {managers.map(m => <option key={m.id} value={m.full_name}>{m.full_name}</option>)}
                </select>
              </div>
              <div><label style={lbl}>Join Date</label><input type="date" style={inp} value={f.join_date} onChange={e => setF({ ...f, join_date: e.target.value })} /></div>
              <div><label style={lbl}>Date to Position</label><input type="date" style={inp} value={f.date_to_position} onChange={e => setF({ ...f, date_to_position: e.target.value })} /></div>
              <div><label style={lbl}>Date to SCT</label><input type="date" style={inp} value={f.date_to_sct} onChange={e => setF({ ...f, date_to_sct: e.target.value })} /></div>
              <div><label style={lbl}>Date to Salary Group</label><input type="date" style={inp} value={f.date_to_salary_group} onChange={e => setF({ ...f, date_to_salary_group: e.target.value })} /></div>
            </div>
          </div>

          {/* Signature */}
          <div style={{ ...card, marginBottom: 22 }}>
            {sectionTitle('✍️', 'Signature', 'Upload a PNG of your signature. It is placed automatically on documents that need it, such as Finance Requests.')}
            <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 240, border: '1px dashed var(--border)', borderRadius: 10, padding: 18, textAlign: 'center', background: 'var(--surface2)', minHeight: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {hasSig ? <img src={`/api/profile/signature?token=${token}&b=${sigBust}`} alt="signature" style={{ maxWidth: '100%', maxHeight: 80 }} /> : <span style={{ fontSize: 12, color: 'var(--muted)' }}>No signature uploaded</span>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 150 }}>
                <label style={{ ...btn('var(--surface2)', ACCENT), cursor: 'pointer', textAlign: 'center', fontSize: 13 }}>{hasSig ? 'Replace PNG' : 'Upload PNG'}<input type="file" accept=".png,image/png" onChange={uploadSig} style={{ display: 'none' }} /></label>
                {hasSig && <button onClick={removeSig} style={{ ...btn('var(--surface2)', '#DC2626'), fontSize: 13 }}>Remove</button>}
              </div>
            </div>
          </div>

          {/* Save bar */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={save} disabled={saving} style={{ ...btn(), padding: '11px 28px', fontSize: 14 }}>{saving ? 'Saving…' : 'Save Information'}</button>
          </div>
        </div>
      </div>
      {toast && <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#111827', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 13, zIndex: 2000 }}>{toast}</div>}
    </div>
  );
}
